import React, { useEffect, useState, useRef, useCallback } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from "../utils/api";
import {
  RefreshCw, Clock, Flame, CheckCircle, ChefHat, Volume2, VolumeX,
  StickyNote, ConciergeBell, Maximize, Minimize, ShoppingBag, Armchair,
} from "lucide-react";

/* ── Column config ──────────────────────────────────────────────────────── */
const COLUMNS = [
  {
    status: "ORDER_PLACED",
    title: "New",
    dot: "bg-blue-500",
    chip: "bg-blue-500/10 text-blue-400 border-blue-500/25",
    action: { to: "PREPARING", label: "Start Prep", icon: Flame, cls: "bg-blue-600 hover:bg-blue-500" },
  },
  {
    status: "PREPARING",
    title: "Preparing",
    dot: "bg-amber-500",
    chip: "bg-amber-500/10 text-amber-400 border-amber-500/25",
    action: { to: "READY", label: "Mark Ready", icon: CheckCircle, cls: "bg-emerald-600 hover:bg-emerald-500" },
  },
  {
    status: "READY",
    title: "Ready for Pickup",
    dot: "bg-emerald-500",
    chip: "bg-emerald-500/10 text-emerald-400 border-emerald-500/25",
    action: { to: "SERVED", label: "Served", icon: ConciergeBell, cls: "bg-violet-600 hover:bg-violet-500" },
  },
];

/* ── Time helpers ───────────────────────────────────────────────────────── */
const minutesSince = (order) => {
  const t = order.createdAt || order.updatedAt;
  if (!t) return 0;
  const d = new Date(t);
  if (isNaN(d.getTime())) return 0;
  return Math.max(0, Math.floor((Date.now() - d) / 60000));
};

// Green < 10 min · Amber 10–20 min · Red 20+ min
const urgency = (mins) => {
  if (mins >= 20) return {
    strip: "bg-red-500",
    card: "border-red-500/60 bg-red-950/20 animate-pulse-slow",
    badge: "bg-red-500/15 text-red-400 border-red-500/30",
  };
  if (mins >= 10) return {
    strip: "bg-amber-500",
    card: "border-amber-500/50 bg-amber-950/10",
    badge: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  };
  return {
    strip: "bg-teal-500",
    card: "border-slate-700 bg-slate-800/80",
    badge: "bg-teal-500/10 text-teal-400 border-teal-500/25",
  };
};

/* ── Ticket card ────────────────────────────────────────────────────────── */
function TicketCard({ order, action, onAction, busy }) {
  const mins = minutesSince(order);
  const u = urgency(mins);
  const isParcel = order.orderType !== "DINE_IN";
  const itemCount = (order.items || []).reduce((s, i) => s + (i.qty || 0), 0);
  const ActionIcon = action.icon;

  return (
    <div className={`relative overflow-hidden border rounded-xl shadow-lg transition-all duration-300 ${u.card}`}>
      {/* Urgency strip */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${u.strip}`} />

      <div className="p-3.5" style={{ paddingLeft: "1.1rem" }}>
        {/* Header: location + timer */}
        <div className="flex justify-between items-start gap-2 mb-2.5">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-[10px] text-slate-400 font-bold uppercase tracking-wider">
              {isParcel ? <ShoppingBag size={11} /> : <Armchair size={11} />}
              {isParcel ? "Takeaway" : "Dine-In"}
              <span className="text-slate-600">·</span>
              <span className="text-slate-500 normal-case font-semibold">#{order.orderNumber}</span>
              {order.kotRound > 1 && (
                <span className="bg-fuchsia-500/15 text-fuchsia-400 border border-fuchsia-500/25 px-1.5 rounded normal-case font-bold">
                  KOT {order.kotRound}
                </span>
              )}
            </div>
            <h3 className="text-lg font-black text-white leading-tight truncate">
              {isParcel
                ? `Parcel P${order.orderNumber}`
                : `${order.sectionName} ${order.unitNumber}`}
              {!isParcel && (
                <span className="text-xs text-slate-400 font-semibold ml-1.5">{order.floorName}</span>
              )}
            </h3>
            {isParcel && order.customer?.name && (
              <p className="text-[11px] text-slate-400 font-semibold truncate">👤 {order.customer.name}</p>
            )}
          </div>
          <div className={`shrink-0 flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-black border ${u.badge}`}>
            <Clock className="w-3 h-3" />
            {mins}m
          </div>
        </div>

        {/* Items */}
        <ul className="space-y-1.5 border-t border-slate-700/50 pt-2.5">
          {order.items?.map((item, idx) => (
            <li key={item._id || `${item.itemId}-${idx}`} className="flex flex-col">
              <div className="flex items-start justify-between gap-2">
                <span className="text-[15px] font-semibold text-slate-100 leading-snug">
                  {item.name}
                </span>
                <span className="shrink-0 min-w-[28px] text-center bg-slate-700 text-white font-black px-1.5 py-0.5 rounded-md text-sm">
                  ×{item.qty}
                </span>
              </div>
              {(item.variant || item.addOns?.length > 0 || item.note) && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {item.variant && (
                    <span className="text-[10px] font-bold text-teal-400 bg-teal-950/40 border border-teal-500/15 px-1.5 py-0.5 rounded">
                      {item.variant.name}
                    </span>
                  )}
                  {item.addOns?.length > 0 && (
                    <span className="text-[10px] font-bold text-amber-400 bg-amber-950/40 border border-amber-500/15 px-1.5 py-0.5 rounded">
                      + {item.addOns.map((a) => a.name).join(", ")}
                    </span>
                  )}
                  {item.note && (
                    <span className="flex items-center gap-1 text-[10px] font-bold text-rose-300 bg-rose-950/40 border border-rose-500/20 px-1.5 py-0.5 rounded">
                      <StickyNote size={9} /> {item.note}
                    </span>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>

        {/* Order-level note */}
        {order.notes && (
          <p className="flex items-start gap-1.5 text-[11px] text-rose-300 bg-rose-950/30 border border-rose-500/15 rounded-lg px-2 py-1.5 mt-2.5">
            <StickyNote size={11} className="shrink-0 mt-0.5" />
            {order.notes}
          </p>
        )}

        {/* Footer: count + action */}
        <div className="flex items-center gap-2 mt-3">
          <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider shrink-0">
            {itemCount} item{itemCount !== 1 ? "s" : ""}
          </span>
          <button
            onClick={() => onAction(order._id, action.to)}
            disabled={busy}
            className={`flex-1 flex items-center justify-center gap-1.5 text-white text-sm font-black py-2 px-3 rounded-lg shadow-md transition-all active:scale-[0.98] disabled:opacity-50 ${action.cls}`}
          >
            <ActionIcon className="w-4 h-4" />
            {action.label}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Dashboard ──────────────────────────────────────────────────────────── */
const KdsDashboard = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [, setTick] = useState(0); // re-render every 30s so timers stay live

  const prevOrderCountRef = useRef(0);

  /* Pleasant 3-chord chime using Web Audio API */
  const playNewOrderBeep = useCallback(() => {
    if (!audioEnabled) return;
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const playTone = (freq, startTime, duration) => {
        const osc = audioCtx.createOscillator();
        const gain = audioCtx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, audioCtx.currentTime + startTime);
        gain.gain.setValueAtTime(0, audioCtx.currentTime + startTime);
        gain.gain.linearRampToValueAtTime(0.2, audioCtx.currentTime + startTime + 0.05);
        gain.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + startTime + duration);
        osc.connect(gain);
        gain.connect(audioCtx.destination);
        osc.start(audioCtx.currentTime + startTime);
        osc.stop(audioCtx.currentTime + startTime + duration);
      };
      playTone(659.25, 0, 0.4);   // E5
      playTone(523.25, 0.2, 0.4); // C5
      playTone(783.99, 0.4, 0.6); // G5
    } catch (e) {
      console.warn("Audio blocked by browser permissions:", e.message);
    }
  }, [audioEnabled]);

  const toggleAudio = () => {
    if (!audioEnabled) {
      try {
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        audioCtx.resume().then(() => {
          setAudioEnabled(true);
          toast.success("Audio alerts enabled!");
        });
      } catch (e) {
        console.error(e);
      }
    } else {
      setAudioEnabled(false);
      toast.info("Audio alerts disabled.");
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.().then(() => setFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen?.().then(() => setFullscreen(false)).catch(() => {});
    }
  };

  useEffect(() => {
    const onFsChange = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  const fetchKitchenOrders = useCallback(async (isSilent = false) => {
    if (!isSilent) setRefreshing(true);
    try {
      const res = await api.get("orders/kitchen");
      if (res.data) {
        const newOrders = res.data;
        setOrders(newOrders);
        const newCount = newOrders.filter((o) => o.status === "ORDER_PLACED").length;
        if (prevOrderCountRef.current > 0 && newCount > prevOrderCountRef.current) {
          playNewOrderBeep();
          toast.info("🛎️ New order received in kitchen!", { autoClose: 3000 });
        }
        prevOrderCountRef.current = newCount;
      }
    } catch (error) {
      console.error("Failed to fetch kitchen orders:", error);
      if (!isSilent) toast.error("Failed to load active kitchen orders");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [playNewOrderBeep]);

  useEffect(() => {
    fetchKitchenOrders();
    const poll = setInterval(() => fetchKitchenOrders(true), 10000);
    const timerTick = setInterval(() => setTick((t) => t + 1), 30000);
    return () => { clearInterval(poll); clearInterval(timerTick); };
  }, [fetchKitchenOrders]);

  const handleUpdateStatus = async (orderId, newStatus) => {
    setBusyId(orderId);
    try {
      await api.patch(`orders/${orderId}/status`, { status: newStatus });
      fetchKitchenOrders(true);
    } catch (error) {
      console.error("Failed to update status:", error);
      toast.error("Failed to update order status");
    } finally {
      setBusyId(null);
    }
  };

  const columnOrders = (status) => orders.filter((o) => o.status === status);
  const lateCount = orders.filter((o) => minutesSince(o) >= 20).length;

  return (
    <div className={`min-h-screen bg-slate-950 text-slate-100 p-4 font-sans ${fullscreen ? "pt-4" : "pt-20"}`}>
      <ToastContainer position="top-right" autoClose={2000} theme="dark" />

      {/* ── Compact header bar ── */}
      <div className="flex flex-wrap items-center gap-3 bg-slate-900 border border-slate-800 rounded-2xl px-4 py-3 mb-4 shadow-xl">
        <div className="flex items-center gap-2.5">
          <div className="p-2 bg-teal-600/10 text-teal-400 rounded-xl border border-teal-500/20">
            <ChefHat className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-lg font-black leading-tight bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent">
              Kitchen Display
            </h1>
            <p className="text-[10px] text-slate-500 leading-none">Auto-refreshes every 10s</p>
          </div>
        </div>

        {/* Live stats */}
        <div className="flex items-center gap-2 ml-2">
          {COLUMNS.map((c) => (
            <span key={c.status} className={`flex items-center gap-1.5 text-xs font-black px-2.5 py-1 rounded-lg border ${c.chip}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
              {columnOrders(c.status).length} {c.title}
            </span>
          ))}
          {lateCount > 0 && (
            <span className="flex items-center gap-1.5 text-xs font-black px-2.5 py-1 rounded-lg border bg-red-500/10 text-red-400 border-red-500/30 animate-pulse">
              ⚠ {lateCount} late
            </span>
          )}
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2 ml-auto">
          <button
            onClick={toggleAudio}
            title={audioEnabled ? "Disable sound alerts" : "Enable sound alerts"}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-xl transition-colors border ${
              audioEnabled
                ? "bg-green-900/20 text-green-400 border-green-800 hover:bg-green-900/30"
                : "bg-slate-800 text-slate-400 border-slate-700 hover:bg-slate-700"
            }`}
          >
            {audioEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            <span className="hidden sm:inline">{audioEnabled ? "Alerts On" : "Alerts"}</span>
          </button>
          <button
            onClick={toggleFullscreen}
            title={fullscreen ? "Exit fullscreen" : "Fullscreen (kitchen TV)"}
            className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 rounded-xl transition-colors"
          >
            {fullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
          </button>
          <button
            onClick={() => fetchKitchenOrders(false)}
            disabled={refreshing}
            title="Refresh now"
            className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 rounded-xl transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center h-[50vh] text-slate-500 gap-4">
          <RefreshCw className="w-12 h-12 animate-spin text-teal-500" />
          <p className="font-semibold">Loading KDS Dashboard...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {COLUMNS.map((col) => {
            const list = columnOrders(col.status);
            return (
              <div key={col.status} className="flex flex-col bg-slate-900/60 border border-slate-800/80 rounded-2xl p-3.5 min-h-[75vh]">
                {/* Column header */}
                <div className="flex justify-between items-center mb-3 border-b border-slate-800 pb-2.5">
                  <h2 className="text-sm font-black text-slate-200 uppercase tracking-wider flex items-center gap-2">
                    <span className={`w-2.5 h-2.5 rounded-full ${col.dot} ${list.length > 0 ? "animate-pulse" : ""}`} />
                    {col.title}
                  </h2>
                  <span className={`text-xs px-2.5 py-0.5 rounded-full font-black border ${col.chip}`}>
                    {list.length}
                  </span>
                </div>

                {list.length === 0 ? (
                  <div className="flex-1 flex flex-col items-center justify-center text-slate-700 gap-2 py-16">
                    <ChefHat className="w-8 h-8" />
                    <p className="text-xs font-semibold text-slate-600">Nothing here</p>
                  </div>
                ) : (
                  <div className="space-y-3 overflow-y-auto pr-1.5" style={{ maxHeight: "calc(100vh - 220px)" }}>
                    {list.map((order) => (
                      <TicketCard
                        key={order._id}
                        order={order}
                        action={col.action}
                        onAction={handleUpdateStatus}
                        busy={busyId === order._id}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default KdsDashboard;
